import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { openAIService } from '@/lib/ai/openai-service';
import { useToast } from '@/hooks/use-toast';
import { Mic, MicOff, Loader2 } from 'lucide-react';

interface VoiceInputProps {
  onTranscription: (text: string) => void;
  isDisabled?: boolean;
  isListening?: boolean;
  onListeningStart?: () => void;
  onListeningEnd?: () => void;
}

export function VoiceInput({ 
  onTranscription, 
  isDisabled = false,
  isListening = false,
  onListeningStart,
  onListeningEnd
}: VoiceInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const { toast } = useToast();

  // Sync external listening state if provided
  useEffect(() => {
    if (isListening && !isRecording) {
      startRecording();
    } else if (!isListening && isRecording) {
      stopRecording();
    }
  }, [isListening]);

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Call external handler if provided
      onListeningStart?.();
      
      // Reset state
      audioChunksRef.current = [];
      setRecordingTime(0);
      
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create the media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Process the recorded audio when stopped
        if (audioChunksRef.current.length > 0) {
          setIsProcessing(true);
          try {
            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
            const transcription = await processAudio(audioBlob);
            if (transcription) {
              onTranscription(transcription);
            }
          } catch (error) {
            console.error('Error processing audio:', error);
            toast({
              variant: 'destructive',
              title: 'Transcription failed',
              description: error instanceof Error ? error.message : 'Failed to transcribe audio',
            });
          } finally {
            setIsProcessing(false);
          }
        }
        
        // Stop all tracks on the stream to release the microphone
        stream.getTracks().forEach(track => track.stop());
        
        // Call external handler if provided
        onListeningEnd?.();
      };
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      
      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        message: 'Microphone access denied. Please enable microphone access.',
      });
      
      // Call external handler if provided
      onListeningEnd?.();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Clear the timer
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const processAudio = async (audioBlob: Blob): Promise<string | null> => {
    try {
      // Check if OpenAI service is configured
      if (!openAIService.isReady()) {
        throw new Error('OpenAI API key is not configured');
      }
      
      // Transcribe the audio using OpenAI's Whisper API
      const transcription = await openAIService.transcribeAudio(audioBlob);
      return transcription;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // If using external listening state control, don't show the button
  if (isListening !== undefined && onListeningStart === undefined) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {isRecording ? (
        <Button
          variant="destructive"
          size="icon"
          className="rounded-full animate-pulse"
          onClick={stopRecording}
          disabled={isProcessing}
        >
          <MicOff className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="icon"
          className="rounded-full"
          onClick={startRecording}
          disabled={isDisabled || isProcessing}
        >
          <Mic className="h-4 w-4" />
        </Button>
      )}
      
      {isRecording && (
        <div className="text-xs font-medium">
          Recording... {formatTime(recordingTime)}
        </div>
      )}
      
      {isProcessing && (
        <div className="flex items-center gap-2 text-xs">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Transcribing...</span>
        </div>
      )}
    </div>
  );
} 